/**
 * Home screen data: selectors that turn cloud records into card rows, and the
 * single write layer used by all three creation paths (+ button, quick line,
 * chat handoff sheets). Keeping writes here means one place for validation
 * and one payload shape that matches what the Flutter app syncs.
 */
import type { CloudTask, CloudNote, ChatSession } from '../types'
import { upsertCloudTask, upsertCloudNote } from './api'

const DONE_STATUSES = new Set([2, 3, 5]) // completed, cancelled, done
const FOLLOWUP_TYPE = 4
const FOLLOWUP_FAMILY_TYPES = new Set([4, 6, 9, 10])
const ALERT_TYPE = 5
const PLAIN_NOTE_TYPES = new Set([0, 1, 2, 3])

// ── Date helpers ────────────────────────────────────────────────

export function sameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function endOfDay(d: Date): Date {
  const e = new Date(d)
  e.setHours(23, 59, 59, 999)
  return e
}

function parseDate(s?: string | null): Date | null {
  if (!s) return null
  const d = new Date(s)
  return isNaN(d.getTime()) ? null : d
}

const pad = (n: number) => String(n).padStart(2, '0')

/** 'YYYY-MM-DD' in local time */
export function toDateInput(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

/** 'HH:MM' in local time */
export function toTimeInput(d: Date): string {
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`
}

/** Combine local date + optional time inputs into a Date */
export function fromInputs(date: string, time?: string): Date | null {
  if (!date) return null
  const d = new Date(`${date}T${time || '00:00'}`)
  return isNaN(d.getTime()) ? null : d
}

/** Next round hour from now — default time for new events and calls */
export function nextRoundHour(now = new Date()): Date {
  const d = new Date(now)
  d.setMinutes(0, 0, 0)
  d.setHours(d.getHours() + 1)
  return d
}

export function formatTime(d: Date): string {
  return d.toLocaleTimeString('fi-FI', { hour: '2-digit', minute: '2-digit' })
}

/** "pe 19.9. klo 10.00" — or "pe 19.9." when there is no meaningful time */
export function formatDateTimeFi(d: Date, withTime = true): string {
  const day = d.toLocaleDateString('fi-FI', { weekday: 'short', day: 'numeric', month: 'numeric' })
  return withTime ? `${day} klo ${formatTime(d)}` : day
}

// ── Row model ───────────────────────────────────────────────────

export interface HomeRow {
  id: string
  title: string
  when: Date | null
  /** Amber "late" / "unanswered" marker */
  flag?: 'overdue' | 'unanswered'
}

function parseJson(content?: string): Record<string, unknown> | null {
  if (!content) return null
  try {
    const p = JSON.parse(content)
    return typeof p === 'object' && p !== null ? p : null
  } catch {
    return null
  }
}

export function taskWhen(t: CloudTask): Date | null {
  return parseDate(t.scheduledTime) || parseDate(t.dueDate)
}

export function reminderWhen(n: CloudNote): Date | null {
  const p = parseJson(n.content)
  if (p && typeof p.date === 'string') {
    const d = new Date(`${p.date} ${(p.time as string) || '00:00'}`)
    if (!isNaN(d.getTime())) return d
  }
  const meta = n.metadata as Record<string, unknown> | undefined
  return parseDate(meta?.scheduled_at as string | undefined)
}

function byWhen(a: HomeRow, b: HomeRow): number {
  return (a.when?.getTime() ?? Infinity) - (b.when?.getTime() ?? Infinity)
}

/** Tänään: open tasks due today or overdue, overdue first. */
export function selectTodayTasks(tasks: CloudTask[], now = new Date()): HomeRow[] {
  const startOfToday = new Date(now)
  startOfToday.setHours(0, 0, 0, 0)
  return tasks
    .filter((t) => t.type !== 1 && !DONE_STATUSES.has(t.status ?? 0))
    .map((t) => ({ t, when: taskWhen(t) }))
    .filter(({ when }) => when && when <= endOfDay(now))
    .map(({ t, when }) => ({
      id: t.id,
      title: t.title,
      when,
      flag: when! < startOfToday ? ('overdue' as const) : undefined,
    }))
    .sort(byWhen)
}

/** Kalenteri: events on today's date. Recurrence is not expanded yet. */
export function selectTodayEvents(tasks: CloudTask[], now = new Date()): HomeRow[] {
  return tasks
    .filter((t) => t.type === 1 && t.status !== 3)
    .map((t) => ({ id: t.id, title: t.title, when: taskWhen(t) }))
    .filter((r) => r.when && sameLocalDay(r.when, now))
    .sort(byWhen)
}

/** Muistutukset: today's scheduled calls (follow-up family) and alerts. */
export function selectTodayReminders(notes: CloudNote[], now = new Date()): HomeRow[] {
  return notes
    .filter((n) => {
      const meta = n.metadata as Record<string, unknown> | undefined
      if (meta?.dismissed === true) return false
      const p = parseJson(n.content)
      if (FOLLOWUP_FAMILY_TYPES.has(n.type ?? -1)) return p?.completed !== true
      if (n.type === ALERT_TYPE) {
        const owner = (meta?.owner ?? meta?.persona_id) as string | undefined
        return p?.enabled !== false && !(typeof owner === 'string' && owner.startsWith('task_'))
      }
      return false
    })
    .map((n) => {
      const p = parseJson(n.content)
      return {
        id: n.id,
        title: n.title || (typeof p?.title === 'string' ? p.title : '') || 'Muistutus',
        when: reminderWhen(n),
        // Unanswered is a normal state, not an error — rendered amber, not red.
        flag: p?.missed === true ? ('unanswered' as const) : undefined,
      }
    })
    .filter((r) => r.when && sameLocalDay(r.when, now))
    .sort(byWhen)
}

export function selectRecentNotes(notes: CloudNote[]): HomeRow[] {
  return notes
    .filter((n) => PLAIN_NOTE_TYPES.has(n.type ?? 0) && !n.is_ai_generated_image && !n.is_ai_generated_document && !n.is_app)
    .map((n) => ({
      id: n.id,
      title: n.title || (n.content || '').split('\n')[0] || 'Muistiinpano',
      when: parseDate(n.last_updated_at || n.created_at),
    }))
    .sort((a, b) => (b.when?.getTime() ?? 0) - (a.when?.getTime() ?? 0))
}

/** Keskustelut: titles and timestamps only — no transcript preview. */
export function selectRecentChats(sessions: ChatSession[]): HomeRow[] {
  return sessions
    // Quick answers have their own card; keep them out of the conversation list.
    .filter((s) => s.session_type !== 'quick_chat')
    .map((s) => ({ id: s.id, title: s.summary || 'Keskustelu', when: parseDate(s.started_at) }))
    .sort((a, b) => (b.when?.getTime() ?? 0) - (a.when?.getTime() ?? 0))
}

// ── Write layer ─────────────────────────────────────────────────

export interface TaskDraft { title: string; date: string; time: string }
export interface EventDraft { title: string; date: string; time: string; durationMin: number; location: string }
export interface ReminderDraft { title: string; date: string; time: string; personaId: string }
export interface NoteDraft { title: string; content: string }

function requireTitle(title: string): string {
  const t = title.trim()
  if (!t) throw new Error('Otsikko puuttuu')
  return t
}

export async function createTask(d: TaskDraft): Promise<CloudTask> {
  const now = new Date().toISOString()
  const due = fromInputs(d.date, d.time)
  const task: CloudTask = {
    id: crypto.randomUUID(),
    title: requireTitle(d.title),
    priority: 1,
    status: 0,
    type: 0,
    dueDate: due ? due.toISOString() : undefined,
    isAllDay: !d.time,
    createdAt: now,
    lastUpdatedAt: now,
  }
  await upsertCloudTask(task)
  return task
}

export async function createEvent(d: EventDraft): Promise<CloudTask> {
  const start = fromInputs(d.date, d.time)
  if (!start) throw new Error('Ajankohta puuttuu')
  const now = new Date().toISOString()
  const event: CloudTask = {
    id: crypto.randomUUID(),
    title: requireTitle(d.title),
    priority: 1,
    status: 0,
    type: 1,
    dueDate: start.toISOString(),
    scheduledTime: start.toISOString(),
    duration: d.durationMin || 60,
    isAllDay: false,
    location: d.location.trim() || undefined,
    createdAt: now,
    lastUpdatedAt: now,
  }
  await upsertCloudTask(event)
  return event
}

export async function createReminder(d: ReminderDraft): Promise<CloudNote> {
  const at = fromInputs(d.date, d.time)
  if (!at) throw new Error('Ajankohta puuttuu')
  const now = new Date().toISOString()
  const note: CloudNote = {
    id: crypto.randomUUID(),
    title: requireTitle(d.title),
    type: FOLLOWUP_TYPE,
    metadata: {
      scheduled_at: at.toISOString(),
      persona_id: d.personaId,
      dismissed: false,
    },
    created_at: now,
    last_updated_at: now,
  }
  await upsertCloudNote(note)
  return note
}

export async function createNote(d: NoteDraft): Promise<CloudNote> {
  const now = new Date().toISOString()
  const note: CloudNote = {
    id: crypto.randomUUID(),
    title: requireTitle(d.title),
    content: d.content.trim() || undefined,
    type: 0,
    created_at: now,
    last_updated_at: now,
  }
  await upsertCloudNote(note)
  return note
}

/** Mark a task completed. Returns the previous record for undo. */
export async function completeTask(task: CloudTask): Promise<CloudTask> {
  const now = new Date().toISOString()
  await upsertCloudTask({ ...task, status: 2, completedAt: now, lastUpdatedAt: now })
  return task
}

/** Write a previous task record back (undo). */
export async function restoreTask(previous: CloudTask): Promise<void> {
  await upsertCloudTask({ ...previous, lastUpdatedAt: new Date().toISOString() })
}

export async function rescheduleTask(task: CloudTask, at: Date): Promise<CloudTask> {
  const updated: CloudTask = {
    ...task,
    dueDate: at.toISOString(),
    scheduledTime: task.scheduledTime || task.type === 1 ? at.toISOString() : task.scheduledTime,
    lastUpdatedAt: new Date().toISOString(),
  }
  await upsertCloudTask(updated)
  return updated
}

export async function rescheduleReminder(note: CloudNote, at: Date): Promise<CloudNote> {
  const meta = { ...(note.metadata || {}), scheduled_at: at.toISOString() }
  const p = parseJson(note.content)
  const content = p && typeof p.date === 'string'
    ? JSON.stringify({ ...p, date: toDateInput(at), time: toTimeInput(at), missed: false })
    : note.content
  const updated: CloudNote = { ...note, metadata: meta, content, last_updated_at: new Date().toISOString() }
  await upsertCloudNote(updated)
  return updated
}

/** Cancel (dismiss) a scheduled call or alert. Not a delete — the record stays. */
export async function cancelReminder(note: CloudNote): Promise<CloudNote> {
  const updated: CloudNote = {
    ...note,
    metadata: { ...(note.metadata || {}), dismissed: true },
    last_updated_at: new Date().toISOString(),
  }
  await upsertCloudNote(updated)
  return note
}

export async function restoreNote(previous: CloudNote): Promise<void> {
  await upsertCloudNote({ ...previous, last_updated_at: new Date().toISOString() })
}
